import React from 'react';
import { Button, useToast } from '@chakra-ui/react';
import { FaFilePdf } from 'react-icons/fa';
import { FiEye } from 'react-icons/fi';
import openPdf, { previewPdf } from '../../utils/openPdf';

// Shared "View PDF" footer button (item 23) — opens a document's branded PDF.
// `path` is the API path, e.g. `proposal/<id>/pdf`. `label` overrides the button text
// (dart 2026-07-14 — used for the "Non-RNWY PDF" export variant).
// item 681(C) — `preview` opens the PDF INLINE in a browser tab (the "eyeball" preview,
// same as the grid/ledger) instead of downloading it; default stays download.
// item 841d (Chad) — flush any pending autosave BEFORE opening the PDF, so the export reflects the
// latest edits (e.g. a just-typed Help Desk tier) instead of the debounced-stale saved state. Blurs
// the focused field to commit it, asks the form scaffold to save now, and waits for the flush (with
// a fallback so the PDF always opens even if no scaffold is listening — e.g. a grid preview).
const flushPendingSave = (): Promise<void> => new Promise((resolve) => {
  let done = false;
  const finish = (): void => {
    if (done) return;
    done = true;
    window.removeEventListener('basix:autosave-flushed', finish);
    resolve();
  };
  window.addEventListener('basix:autosave-flushed', finish);
  (document.activeElement as HTMLElement | null)?.blur?.();
  // let React process the field's onBlur → form.change → the scaffold's value tracking, then flush
  window.setTimeout(() => window.dispatchEvent(new Event('basix:flush-autosave')), 60);
  window.setTimeout(finish, 3000); // fallback: never block the PDF
});

// item 1033 — the proposal footer folds its PDF actions into a menu; menu items reuse this
// flush-then-open behavior so they act exactly like the button always has. Rejects on failure
// so the caller owns the error toast.
export const openDocumentPdf = async (path: string, preview?: boolean): Promise<void> => {
  await flushPendingSave();
  return preview ? previewPdf(path) : openPdf(path);
};

const ViewPdfButton: React.FC<{ path: string; label?: string; preview?: boolean }> = ({ path, label, preview }) => {
  const toast = useToast();
  return (
    <Button
      size="sm"
      variant="ghost"
      leftIcon={preview ? <FiEye /> : <FaFilePdf />}
      onClick={() => openDocumentPdf(path, preview).catch(() => toast({
        status: 'error', title: 'Could not open the PDF', duration: 6000, isClosable: true,
      }))}
    >
      {label ?? (preview ? 'Preview PDF' : 'Download PDF')}
    </Button>
  );
};

ViewPdfButton.defaultProps = { label: undefined, preview: false };

export default ViewPdfButton;
